import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { Product } from '../types/product.type';
import { ADMIN_PRODUCTS_API_PATHS } from '../constants/products-api-paths.constants';

type UseAdminGetProductByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  product: Product | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetProductByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  product: null,
};

export function useAdminGetProductById({ productId }: { productId: string }): UseAdminGetProductByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<Product | undefined>(ADMIN_PRODUCTS_API_PATHS.GET_PRODUCT_BY_ID(productId));

      setResult({ isLoading: false, isRefetching: false, product: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération du produit'),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [productId, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
